import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

// Lecteur plein écran du showreel — simple fondu au noir, la vidéo remplit
// vraiment la fenêtre (fixed inset-0, pas de boîte à aspect-ratio imbriquée
// ni de décalage codé en dur comme dans l'ancienne version).
//
// Rendu via un portail vers document.body (comme VideoModal/Lightbox/
// BreakdownVideo dans ProjectDetail.jsx) plutôt qu'inline dans l'arbre
// de Portfolio : sans ça, le Header (position fixed, tout en haut de
// l'arbre React) s'affichait PAR-DESSUS le bouton fermer malgré un
// z-index pourtant plus bas (50 contre 100) — le style inline
// "opacity" de ce composant (0 au repos, transition vers 1) crée son
// propre contexte d'empilement, qui semble alors se faire recouvrir
// par le Header dans certains cas plutôt que le recouvrir. Un portail
// place ce composant en dernier enfant direct de <body>, hors de tout
// ancêtre commun avec le Header : plus aucune ambiguïté possible.
const ShowreelOverlay = ({ videoSrc, playFull, onClose }) => {
  const videoRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (playFull) {
      // Sans "loop", une réouverture après lecture complète repartirait
      // du dernier instant (figé sur la dernière image) sans ce reset.
      if (videoRef.current && videoRef.current.ended) {
        videoRef.current.currentTime = 0;
      }
      videoRef.current?.play();
      // Le composant reste monté en permanence (juste opacity 0 / pointer-events
      // none au repos) : pas besoin de décaler d'une frame pour que la
      // transition s'anime, l'élément existe déjà dans le DOM des deux côtés
      // du changement d'état.
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [playFull]);

  const handleClose = () => {
    setVisible(false);
    videoRef.current?.pause();
    setTimeout(() => onClose?.(), 500); // doit correspondre à la durée du fondu
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
      style={{
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.5s ease',
      }}
      onClick={handleClose}
    >
      <video
        ref={videoRef}
        src={videoSrc}
        playsInline
        preload="none"
        className="w-full h-full object-contain"
      />
      <button
        onClick={(e) => { e.stopPropagation(); handleClose(); }}
        className="absolute top-6 right-6 text-paper/70 hover:text-ember transition-colors"
        aria-label="Fermer le showreel"
      >
        <X size={32} />
      </button>
    </div>,
    document.body
  );
};

export default ShowreelOverlay;
